// The WELCOME PACK section on a subcontractor's page (Session AUY). ADMIN ONLY (AUY fix-up, defect 4): the page renders
// it only for an admin, as it does the rate card section, because Schedule 1 and Your Rates state every rate and the
// locked AUU rule is that the office role cannot read the money; the server refuses the documents and the preparation
// to anyone else.
//
// What it shows: whether the pack can be prepared (the rate card, the email), what is still missing for a pack prepared
// now (the signed agreement, a portal login, the registration, the status, the Director's confirmation of the agreement
// wording), the four documents each as a PDF preview download, and when the pack was last prepared and by whom.
//
// What it does: "Prepare pack and draft the email" asks the server what it would do (the recipient, the attachments, which
// agreement goes in and the warnings), shows that in a branded confirm naming the recipient, and only then prepares it:
// the three guidance documents rendered from the subcontractor's own rate card, the agreement as it was signed or sent
// (never a fresh render; a pre-send copy only when nothing has been sent, marked DRAFT; none when the copy cannot be
// retrieved, said plainly), filed to Drive, the welcome email put in GMAIL DRAFTS with them attached. Nothing is sent
// from here. The office may prepare it again at any time (regeneration is the same button);
// every preparation is audited server side.
//
// Shared components only (Button, Loader, StatusPill), dates through lib/format, no hand-typed CTA, no left stripe.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { SubcontractorWelcomePackReadiness, WelcomePackAgreementSource, WelcomePackDocumentKey } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { ModalShell, MODAL_ACTION_BTN, MODAL_ACTIONS, MODAL_SECONDARY_BTN } from "../../components/ModalShell";
import { formatDateTime } from "../../lib/format";

type Plan = { to: string | null; subject: string; attachments: string[]; agreementSource: WelcomePackAgreementSource; agreementNote: string; warnings: string[]; blockers: string[] };

function Fact({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-base px-3 py-2">
      <div className="font-menu text-[10px] uppercase tracking-wide text-ele-blue">{label}</div>
      <div className={`mt-0.5 text-sm ${ok === false ? "text-ele-danger" : "text-ele-strong"}`}>{value}</div>
    </div>
  );
}

export function SubcontractorWelcomePackSection({ subId, onPrepared }: { subId: string; onPrepared?: () => void }) {
  const [view, setView] = useState<SubcontractorWelcomePackReadiness | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState<WelcomePackDocumentKey | "plan" | "prepare" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);

  const load = useCallback(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.subcontractors.welcomePack.get(subId))
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [subId]);

  useEffect(() => load(), [load]);

  async function download(key: WelcomePackDocumentKey) {
    setBusy(key);
    setError(null);
    try {
      await api.subcontractors.welcomePack.download(subId, key);
    } catch (e) {
      setError(friendlyApiError(e, "The document could not be generated."));
    } finally {
      setBusy(null);
    }
  }

  async function askPlan() {
    setBusy("plan");
    setError(null);
    setMessage(null);
    try {
      const res = await api.subcontractors.welcomePack.prepare(subId, true);
      if (res.preview) setPlan({ to: res.to, subject: res.subject, attachments: res.attachments, agreementSource: res.agreementSource, agreementNote: res.agreementNote, warnings: res.warnings, blockers: res.blockers });
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the pack. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function prepare() {
    setBusy("prepare");
    setError(null);
    try {
      const res = await api.subcontractors.welcomePack.prepare(subId, false);
      if (!res.preview) {
        setPlan(null);
        setMessage(`The welcome email is in Gmail Drafts for ${res.to}, with ${res.attachments.length} documents attached. Review it there and send it.${res.filedToDrive ? "" : " The pack was not filed to Drive."}`);
        load();
        onPrepared?.();
      }
    } catch (e) {
      setError(friendlyApiError(e, "The pack could not be prepared."));
      setPlan(null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Welcome pack">
      <h2 className="mb-3 font-heading text-lg uppercase tracking-wide text-ele-blue">Welcome pack</h2>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load the welcome pack.</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ele-body">
            The Subcontractor Agreement (with Schedule 1), Your Rates, a short FAQ and Getting Set Up, every figure from this subcontractor&apos;s own rate card. Preparing the pack
            puts the welcome email in Gmail Drafts with the four documents attached; nothing is sent from here.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Fact label="Rate card" value={view.rateCard.present ? `Version ${view.rateCard.version}, from ${view.rateCard.effectiveFromLabel}` : "None in force"} ok={view.rateCard.present} />
            <Fact label="Status" value={view.statusLabel} />
            <Fact
              label="Agreement"
              value={
                view.agreement.status === "signed"
                  ? `Signed ${formatDateTime(view.agreement.signedAt)}${view.agreement.templateLabel ? ` (${view.agreement.templateLabel})` : ""}`
                  : view.agreement.status === "sent"
                    ? `Sent for signature ${formatDateTime(view.agreement.sentAt)}`
                    : "Not sent"
              }
            />
            <Fact label="Portal login" value={view.portal.logins === 0 ? "None yet" : `${view.portal.logins} (${view.portal.signedIn} signed in)`} />
          </div>
          {!view.agreement.wordingConfirmed && (
            <p className="rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-amber-wash-ink">
              The agreement wording is awaiting the Director&apos;s confirmation of its solicitor review (Settings, Subcontractor documents). The agreement carries &quot;DRAFT, NOT FOR SIGNATURE&quot; until then and cannot be sent for signature.
            </p>
          )}
          {view.blockers.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-ele-danger">
              {view.blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {view.documents.map((d) => (
              <Button key={d.key} size="sm" variant="secondary" loading={busy === d.key} disabled={!view.rateCard.present || (busy != null && busy !== d.key)} onClick={() => download(d.key)}>
                Preview {d.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" loading={busy === "plan"} disabled={view.blockers.length > 0 || busy != null} onClick={askPlan}>
              {view.preparedAt ? "Prepare the pack again" : "Prepare pack and draft the email"}
            </Button>
            {view.preparedAt && (
              <span className="text-xs text-ele-body">
                Last prepared {formatDateTime(view.preparedAt)}
                {view.preparedByName ? ` by ${view.preparedByName}` : ""}
                {view.preparedRateCardVersion != null ? `, rate card version ${view.preparedRateCardVersion}` : ""}.
              </span>
            )}
          </div>
          {message && <p className="text-sm text-ele-green">{message}</p>}
          {error && (
            <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {error}
            </p>
          )}
        </div>
      )}

      {plan && (
        <ModalShell onClose={() => busy !== "prepare" && setPlan(null)} ariaLabel="Prepare the welcome pack">
          <h3 className="mb-2 font-heading text-lg text-ele-strong">Prepare the welcome pack?</h3>
          <p className="text-sm text-ele-body">
            This drafts the welcome email to <strong className="text-ele-strong">{plan.to ?? "no address on file"}</strong> in Gmail Drafts, subject &quot;{plan.subject}&quot;, with these documents attached. Nothing is sent until you send it from Gmail.
          </p>
          <ul className="mt-2 list-disc pl-5 text-sm text-ele-strong">
            {plan.attachments.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ele-body">{plan.agreementNote}</p>
          {plan.warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-sm text-ele-amber-wash-ink">
              {plan.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          <div className={MODAL_ACTIONS}>
            <button type="button" className={`${MODAL_SECONDARY_BTN} ${MODAL_ACTION_BTN}`} onClick={() => setPlan(null)} disabled={busy === "prepare"}>
              Cancel
            </button>
            <Button variant="primary" className={MODAL_ACTION_BTN} loading={busy === "prepare"} disabled={plan.blockers.length > 0} onClick={prepare}>
              Draft the welcome email
            </Button>
          </div>
        </ModalShell>
      )}
    </section>
  );
}
